import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle, ArrowRightLeft, Briefcase, Building2, CalendarDays, ClipboardList, KeyRound, Laptop,
  Mail, MapPin, Phone, RotateCcw, UserRound,
} from 'lucide-react';
import { Card } from '../Card';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { EmptyState } from '../EmptyState';
import { Avatar, AvatarFallback } from '../Avatar';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../Dialog';
import { useEmployeeAssetsStore, type AssetEmployee, type EmployeeHoldings } from '../../store/employeeAssetsStore';
import { useIsAssetManager } from '../../hubs/assets/requests/requestUi';

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';

const fieldClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';

function Detail({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | null }) {
  return (
    <div className="flex min-w-0 items-start gap-2">
      <span className="mt-0.5 text-muted-foreground [&_svg]:size-3.5">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate text-sm text-foreground">{value || '—'}</p>
      </div>
    </div>
  );
}

/**
 * Employee details from the People hub, shown wherever an asset is assigned
 * or held. `showProfileLink` links to the People profile.
 */
export function EmployeeCard({ employee, showProfileLink = true, className = '' }: {
  employee: AssetEmployee; showProfileLink?: boolean; className?: string;
}) {
  const inactive = employee.status === 'terminated' || employee.status === 'resigned';
  return (
    <div className={`rounded-lg border border-border bg-card p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <Avatar className="size-10">
          <AvatarFallback className="bg-primary/10 text-primary-strong">{initials(employee.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-foreground">{employee.name}</p>
            {employee.code && <span className="text-xs text-muted-foreground">{employee.code}</span>}
            {inactive && <Badge variant="destructive">{employee.status}</Badge>}
            {!employee.userId && <Badge variant="warning">No user account</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">
            {[employee.designation, employee.department].filter(Boolean).join(' · ') || 'No designation or department set'}
          </p>
        </div>
        {showProfileLink && (
          <Link to={`/people/employees/${employee.id}`} className="shrink-0 text-sm text-primary hover:underline">Profile</Link>
        )}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Detail icon={<Mail />} label="Email" value={employee.email} />
        <Detail icon={<Phone />} label="Phone" value={employee.phone} />
        <Detail icon={<MapPin />} label="Location" value={employee.location} />
        <Detail icon={<UserRound />} label="Reports to" value={employee.managerName} />
        <Detail icon={<CalendarDays />} label="Joined" value={employee.joinDate} />
        <Detail icon={<Briefcase />} label="Currently holds" value={`${employee.assetCount} asset(s) · ${employee.licenseCount} licence seat(s)`} />
      </div>
    </div>
  );
}

const ACTION_LABEL: Record<string, string> = {
  assigned: 'Assigned', returned: 'Returned', license_assigned: 'Seat assigned', license_unassigned: 'Seat released',
};

/**
 * Everything an employee holds from the Asset Hub: devices, licence seats,
 * open requests and recent activity. Asset managers can recover everything in
 * one step (used during offboarding).
 */
export function EmployeeHoldingsPanel({ employeeId, onChange, allowAssign = true }: {
  employeeId: string;
  onChange?: (h: EmployeeHoldings) => void;
  /** False where assigning makes no sense (e.g. offboarding). */
  allowAssign?: boolean;
}) {
  const { fetchHoldings, recover } = useEmployeeAssetsStore();
  const isManager = useIsAssetManager();
  const [data, setData] = useState<EmployeeHoldings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [form, setForm] = useState({ condition: 'Good', notes: '' });
  const [busy, setBusy] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const load = useCallback(async () => {
    try {
      const h = await fetchHoldings(employeeId);
      setData(h);
      setError(null);
      onChangeRef.current?.(h);
    } catch (e: any) {
      setError(e.message);
    }
  }, [employeeId, fetchHoldings]);

  useEffect(() => { load(); }, [load]);

  const doRecover = async () => {
    setBusy(true);
    try {
      const r = await recover(employeeId, { condition: form.condition, notes: form.notes || undefined });
      setData(r.holdings);
      onChangeRef.current?.(r.holdings);
      setRecovering(false);
      const parts = [];
      if (r.returnedAssets.length) parts.push(`${r.returnedAssets.length} asset(s) returned to stock`);
      if (r.releasedLicenses.length) parts.push(`${r.releasedLicenses.length} licence seat(s) released`);
      toast.success(parts.join(', ') || 'Nothing to recover');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return <Card><EmptyState className="m-4 border-0" icon={<AlertTriangle />} title="Could not load assets" description={error} /></Card>;
  }
  if (!data) return <Card><p className="p-6 text-center text-sm text-muted-foreground">Loading assets…</p></Card>;

  const holdsSomething = data.assets.length > 0 || data.licenses.length > 0;
  const inactive = data.employee.status === 'terminated' || data.employee.status === 'resigned';

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="flex items-center gap-2 font-display text-card-title text-foreground">
            <Laptop className="size-4" /> Assigned assets <Badge variant={data.assets.length ? 'primary' : 'muted'}>{data.assets.length}</Badge>
          </h2>
          {isManager && (
            <div className="flex gap-2">
              {holdsSomething && (
                <Button size="sm" variant="secondary" onClick={() => setRecovering(true)}><RotateCcw /> Recover all</Button>
              )}
              {allowAssign && !inactive && (
                <Link to={`/assets/assign?employee=${employeeId}`}>
                  <Button size="sm"><ArrowRightLeft /> Assign asset</Button>
                </Link>
              )}
            </div>
          )}
        </div>
        {data.assets.length === 0 ? (
          <EmptyState className="m-4 border-0" icon={<Laptop />} title="No assets assigned" description="Devices handed to this employee in the Asset Hub appear here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-semibold">Asset</th>
                  <th className="px-4 py-2 font-semibold">Category</th>
                  <th className="px-4 py-2 font-semibold">Serial number</th>
                  <th className="px-4 py-2 font-semibold">Assigned</th>
                  <th className="px-4 py-2 font-semibold">Warranty until</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.assets.map((a) => (
                  <tr key={a.id} className="hover:bg-secondary/30">
                    <td className="px-4 py-2">
                      <Link to={`/assets/registry/${a.assetCode}`} className="font-medium text-primary hover:underline">{a.assetCode}</Link>
                      <p className="text-xs text-muted-foreground">{[a.name, a.manufacturer, a.model].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{a.category || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{a.serialNumber || '—'}</td>
                    <td className="px-4 py-2 tabular-nums text-muted-foreground">{a.assignedDate || '—'}</td>
                    <td className="px-4 py-2 tabular-nums text-muted-foreground">{a.warrantyEndDate || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="flex items-center gap-2 font-display text-card-title text-foreground">
              <KeyRound className="size-4" /> Software licences <Badge variant={data.licenses.length ? 'primary' : 'muted'}>{data.licenses.length}</Badge>
            </h2>
            {isManager && <Link to="/assets/licenses" className="text-sm text-primary hover:underline">Manage</Link>}
          </div>
          {data.licenses.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              {data.employee.userId ? 'No licence seats assigned.' : 'This employee has no user account, so licence seats cannot be assigned.'}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {data.licenses.map((l) => (
                <li key={l.assignmentId} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{l.softwareName}</p>
                    <p className="text-xs text-muted-foreground">{[l.publisher, l.licenseType, l.deviceCode && `on ${l.deviceCode}`].filter(Boolean).join(' · ') || '—'}</p>
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{l.expiryDate ? `Expires ${l.expiryDate}` : 'No expiry'}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="flex items-center gap-2 font-display text-card-title text-foreground">
              <ClipboardList className="size-4" /> Open requests <Badge variant={data.requests.length ? 'warning' : 'muted'}>{data.requests.length}</Badge>
            </h2>
          </div>
          {data.requests.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">No pending or approved asset requests.</p>
          ) : (
            <ul className="divide-y divide-border">
              {data.requests.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                  <span className="text-foreground">{r.assetType} <span className="text-xs text-muted-foreground">· {r.priority}</span></span>
                  <Badge variant={r.status === 'approved' ? 'info' : 'warning'}>{r.status === 'approved' ? 'Approved — awaiting device' : 'Pending approval'}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card>
        <div className="border-b border-border px-4 py-3">
          <h2 className="flex items-center gap-2 font-display text-card-title text-foreground"><Building2 className="size-4" /> Asset activity</h2>
        </div>
        {data.history.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">No asset activity for this employee yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {data.history.map((h) => (
              <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
                <div className="min-w-0">
                  <p className="text-foreground">
                    <Badge variant="muted" className="mr-2">{ACTION_LABEL[h.action] || h.action.replace(/_/g, ' ')}</Badge>
                    {h.details}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground">{new Date(h.createdAt).toLocaleString()}{h.performedByName ? ` · ${h.performedByName}` : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={recovering} onOpenChange={(o) => !o && setRecovering(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Recover everything from {data.employee.name}?</DialogTitle>
            <DialogDescription>
              {data.assets.length} asset(s) will be returned to stock and {data.licenses.length} licence seat(s) released. Each step is recorded in asset history.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <label>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Condition on return</span>
              <select className={fieldClass} value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
                {['Good', 'Fair', 'Damaged', 'Needs repair'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Note</span>
              <input className={fieldClass} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Recovered during offboarding" maxLength={300} />
            </label>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRecovering(false)} disabled={busy}>Cancel</Button>
            <Button onClick={doRecover} isLoading={busy}>Recover all</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
